import { Copy, ExternalLink, UserCircle, Mail, X, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { useFileExplorer } from "@/providers/file-explorer-provider";
import toast from "react-hot-toast";

import { Credenza, CredenzaBody, CredenzaContent, CredenzaDescription, CredenzaFooter, CredenzaHeader, CredenzaTitle } from "@/components/shared/credenza";
import Image from "next/image";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Button } from "../ui/button";

const get_user_email = (user: any) =>
	user?.email ||
	user?.primary_email_address?.email_address ||
	user?.primaryEmailAddress?.emailAddress ||
	user?.email_addresses?.[0]?.email_address ||
	user?.emailAddresses?.[0]?.emailAddress ||
	"";

const get_user_first_name = (user: any) =>
	user?.first_name ||
	user?.firstName ||
	user?.full_name?.split(" ")?.[0] ||
	user?.fullName?.split(" ")?.[0] ||
	"Owner";

const get_share_origin = () => {
	if (typeof window !== "undefined") return window.location.origin;
	return process.env.NEXT_PUBLIC_URL?.replace(/\/+$/, "") || "https://studystash.com";
};

const Share = ({ share_open, set__share_open, user }) => {
	const { current_file } = useFileExplorer();
	const current_user_email = get_user_email(user);
	const current_user_first_name = get_user_first_name(user);

	const [sharedUsers, setSharedUsers] = useState<
		{
			first_name: string;
			last_name: string;
			email?: string;
			id;
		}[]
	>([]);

	const [invitedEmails, setInvitedEmails] = useState<string[]>([]);
	const [isPublic, setIsPublic] = useState(false);

	// set slug from url (every time url changes)
	const [url, set__url] = useState<string>("");

	const updateUsers = async () => {
		if (current_file) {
			// If it's a folder, it might be a subfolder so let's get the last element (the current folder we're in)
			const slug = current_file.type === "folder" ? current_file.slug[current_file.slug.length - 1] : current_file.slug;

			try {
				const res = await fetch(`/api/share/${current_file.type}/${slug}`, {
					method: "GET"
				});

				if (!res.ok) throw new Error("Failed to fetch sharing status");
				const res_data = await res.json();

				setSharedUsers(res_data.users);
				setInvitedEmails(res_data.emails);
				setIsPublic(res_data.isPublic);
			} catch (error) {
				console.error("Get sharing status: ", error);
			}
		}
	};

	useEffect(() => {
		if (!share_open) return;

		const origin = get_share_origin();

		if (!current_file) return set__url(origin);

		const { type: resource_type, slug } = current_file;

		switch (resource_type) {
			case "resource":
				set__url(`${origin}/study/${slug}`);
				updateUsers();
				break;
			case "mindmap":
				set__url(`${origin}/mindmaps/${slug}`);
				updateUsers();
				break;
			case "folder":
				set__url(`${origin}/app/${slug.join("/")}`);
				updateUsers();
				break;
			default:
				set__url("");
				break;
		}
	}, [current_file, share_open]);

	const [email, set__email] = useState<string>("");

	const invite = () => {
		// check if email is valid
		if (!email.includes("@") || !email.includes(".") || email.length < 5 || email.length > 254 || email.includes(" ")) {
			return toast.error("Please enter a valid email address");
		}

		if (current_user_email && current_user_email === email) return toast.error(`You can't invite yourself, silly goose 🦆`);

		if (invitedEmails.includes(email)) {
			return toast.error(`You have already invited this user`, {
				style: {
					maxWidth: "270px"
				}
			});
		}

		set__email("");

		const promise = fetch("/api/share", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				resource: { ...current_file, url },
				invitee_email: email,
			})
		})
			.then(async (res) => {
				if (!res.ok) throw new Error("Failed to send invite");
				await updateUsers();
			})
			.catch(console.error);

		toast.promise(promise, {
			loading: "Sending invite...",
			success: "Sent an invite 😎",
			error: "Failed to send an invite!"
		});
	};

	const [is_deleting_user, set__is_deleting_user] = useState(false);

	const removeEmail = async (email: string) => {
		if (!current_file) return;

		set__is_deleting_user(true);

		const slug = current_file.type === "folder" ? current_file.slug[current_file.slug.length - 1] : current_file.slug;
		await fetch(`/api/share/${current_file.type}/${slug}/${email}/email`, {
			method: "DELETE"
		});

		await updateUsers();

		set__is_deleting_user(false);
	};

	const removeUser = async (user_id: string) => {
		if (!current_file) return;

		set__is_deleting_user(true);

		try {
			const slug = current_file.type === "folder" ? current_file.slug[current_file.slug.length - 1] : current_file.slug;
			await fetch(`/api/share/${current_file.type}/${slug}/${user_id}/user_id`, {
				method: "DELETE"
			});

			await updateUsers();
			set__is_deleting_user(false);
		} catch (error) {
			toast.error("Failed to remove user");
			console.error(error);
			set__is_deleting_user(false);
		}
	};

	const toggle_visibility = async (value: string) => {
		const is_public = value === "anyone" ? true : false;

		setIsPublic(is_public);

		const res = fetch(`/api/share/${current_file.type}/${current_file.slug}/public`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ public: is_public })
		}).then(async (response) => {
			if (!response.ok) throw new Error("Failed to update visibility");
			return response.json();
		});

		toast.promise(res, {
			loading: "Updating visibility...",
			success: `File is now ${is_public ? "public" : "private"}`,
			error: "Failed to update visibility"
		});
	};

	return (
		<Credenza
			open={share_open}
			onOpenChange={set__share_open}
		>
			<CredenzaContent className="max-h-full">
				<CredenzaHeader>
					<div className="relative my-1 flex items-center justify-center">
						<Image
							src={
								current_file?.type === "mindmap" ?
									"/icons/icons8-graph-clique.svg"
									: current_file?.type === "resource" ?
										"/icons/icons8-book.svg"
										: "/icons/icons8-live-folder.png"
							}
							alt={
								current_file?.type === "mindmap" ?
									"Mindmap icon"
									: current_file?.type === "resource" ?
										"Resource icon"
										: "Folder icon"
							}
							className="rounded-xl border bg-gray-50 p-1"
							width={50}
							height={50}
						/>
					</div>

					<CredenzaTitle className="text-center text-xl!">
						Share{" "}
						{current_file?.type ?
							<span className="font-semibold">
								{current_file?.type === "mindmap" ?
									"your mindmap"
									: current_file?.type === "resource" ?
										"your resource"
										: "your folder"}
							</span>
							: "with others"}
					</CredenzaTitle>

					<CredenzaDescription className="my-0! text-center text-lg!">
						Invite others to access this {current_file?.type === "resource" ? "resource" : current_file?.type}
					</CredenzaDescription>
				</CredenzaHeader>

				<CredenzaBody className="overflow-auto">
					{(current_file && current_file.type !== "folder") && (
						<div>
							<div className="my-4 flex items-center justify-between rounded-md border border-gray-200 p-3">
								<div className="flex items-center space-x-3 overflow-hidden">
									<span className="rounded-md bg-blue-100 p-2.5 text-lg text-blue-500">
										{isPublic ?
											<ExternalLink />
											: <Users />}
									</span>
									<div className="flex flex-col space-y-1 overflow-hidden">
										<Select
											onValueChange={toggle_visibility}
											defaultValue={isPublic ? "anyone" : "specific"}
										>
											<SelectTrigger className="h-6 w-48 px-1 text-sm font-medium">
												<SelectValue placeholder="Select visibility" />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="anyone">Anyone with the link</SelectItem>
												<SelectItem value="specific">People with access</SelectItem>
											</SelectContent>
										</Select>

										<div className="flex flex-row items-center space-x-2">
											<span className="truncate text-xs text-gray-500">{url}</span>
										</div>
									</div>
								</div>

								<button
									className="text-lg font-medium text-gray-500 transition-all hover:text-gray-600"
									onClick={() => {
										navigator.clipboard.writeText(url);
										toast.success("Copied to clipboard!", {
											style: {
												maxWidth: "270px"
											}
										});
									}}
								>
									<Copy />
								</button>
							</div>

							<hr className="my-6 h-px w-full bg-gray-200" />
						</div>
					)}

					<div className="w-full flex flex-col">
						<label htmlFor="share-email-input" className="mb-1 text-sm font-medium text-gray-900">
							Enter an email address to invite
						</label>
						<div className="flex w-full">
							<input
								id="share-email-input"
								className={`w-full rounded-l-md border p-2 text-sm outline-hidden transition-colors duration-200 ${email ? "border-blue-500" : "border-gray-300"
									}`}
								placeholder="Enter an email address..."
								type="email"
								onChange={(e) => set__email(e.target.value)}
								value={email}
								onKeyPress={(e) => {
									if (e.key === "Enter") invite();
								}}
								aria-label="Enter an email address to invite"
							/>
							<button
								className={`${email ? "bg-blue-500 hover:bg-blue-600" : "cursor-not-allowed bg-gray-300"
									} rounded-r-md px-4 py-2 text-sm font-semibold text-white transition-colors duration-200`}
								onClick={invite}
								disabled={!email}
							>
								Invite
							</button>
						</div>
					</div>

					<h3 className="my-4 text-base font-medium text-gray-900">People with access</h3>

					<div className="my-4 max-h-48 space-y-3 overflow-auto">
						<div className="flex items-center justify-between">
							<div className="flex items-center space-x-3">
								<UserCircle className="size-8 text-gray-400" />

									<div className="flex flex-col">
										<p className="text-sm font-medium">{current_user_first_name}</p>
										<p className="text-xs text-gray-500">{current_user_email || "No email available"}</p>
									</div>
								</div>

							<span className="rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">Owner</span>
						</div>

						{sharedUsers.map((user) => (
							<div
								key={`${user.id}-${user.email}`}
								className="flex items-center justify-between"
							>
								<div className="flex items-center space-x-3">
									<UserCircle className="size-8 text-gray-400" />

									<div>
										{/* \TODO-AUTH */}
										{/* <p className="text-sm font-medium">
											{/* {user.first_name} {user.last_name} </p> */}
										<p className="text-xs text-gray-500">{user.email}</p>
									</div>
								</div>

								<button
									className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
									onClick={() => removeUser(user.id)}
									disabled={is_deleting_user}
								>
									<X />
								</button>
							</div>
						))}

						{invitedEmails.map((email) => (
							<div
								key={`${email}-invited`}
								className="flex items-center justify-between"
							>
								<div className="flex items-center space-x-3">
									<Mail className="size-8 text-gray-400" />
									<div>
										<p className="text-sm font-medium">Invited user</p>
										<p className="text-xs text-gray-500">{email}</p>
									</div>
								</div>

								<button
									className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
									onClick={() => removeEmail(email)}
									disabled={is_deleting_user}
								>
									<X />
								</button>
							</div>
						))}
					</div>
				</CredenzaBody>

				<CredenzaFooter>
					<Button
						variant="outline"
						className='w-28 disabled:cursor-not-allowed disabled:opacity-50'
						onClick={() => set__share_open(false)}
					>
						<p>Cancel</p>
					</Button>
				</CredenzaFooter>
			</CredenzaContent>
		</Credenza>
	);
};

export default Share;
